/**
 * Utility functions for Heat Pump Simulation calculations
 */

import {
  CP_AIR,
  CP_WATER,
  D_AIR,
  D_WATER,
  DEFAULT_COMPRESSOR_EFFICIENCY,
  DEFAULT_FRICTION_COEFFICIENT,
  VALVE_FLOW_RATES,
  RADIATOR_POWER_MATRIX,
  RADIATOR_EXPONENT,
} from "./constants";

/**
 * Calculate Coefficient of Performance (COP) for heat pump
 * @param {number} supplyTemperature - Supply water temperature (°C)
 * @param {number} outsideTemperature - Outside air temperature (°C)
 * @returns {number} COP value
 */
export function calculateCOP(supplyTemperature, outsideTemperature) {
  const temperatureOffset = 5;
  const effectiveTemperature = temperatureOffset + supplyTemperature;

  const cop =
    (DEFAULT_COMPRESSOR_EFFICIENCY * (effectiveTemperature + 273)) /
    (effectiveTemperature - (outsideTemperature - temperatureOffset));

  return cop;
}

/**
 * Get valve flow rate based on position
 * @param {number} valvePosition - Valve position (0-1, representing positions 1-7)
 * @returns {number} Flow rate in LPM
 */
export function getValveFlowRate(valvePosition) {
  const position = Math.round(valvePosition * 7);
  return VALVE_FLOW_RATES[position] || 0;
}

/**
 * Calculate nominal radiator power
 * @param {number} radiatorCount - Number of radiator sections
 * @param {string} radiatorType - Type code (e.g., "22")
 * @returns {number} Nominal power in watts
 */
export function calculateRadiatorPower(radiatorCount, radiatorType) {
  const length = 100 * radiatorCount;
  return RADIATOR_POWER_MATRIX[length]?.[radiatorType] || 0;
}

/**
 * Calculate volumetric flow rate through pipes
 * @param {number} systemPressure - System pressure (bar)
 * @param {number} pipeLength - Effective pipe length (m)
 * @param {number} pipeDiameter - Pipe diameter (mm)
 * @returns {number} Volumetric flow rate (m³/s)
 */
export function calculateVolumetricFlow(systemPressure, pipeLength, pipeDiameter) {
  const pressurePascals = systemPressure * 100 * 1000;
  const diameterMeters = pipeDiameter * 0.001;
  const pipeArea = Math.PI * diameterMeters * diameterMeters * 0.25;

  const flowVelocity = Math.sqrt(
    (2 * diameterMeters * pressurePascals) /
      (DEFAULT_FRICTION_COEFFICIENT * pipeLength * D_WATER)
  );

  return pipeArea * flowVelocity;
}

/**
 * Calculate room volume from building envelope areas
 * Estimates volume using floor area (roof) and wall area to determine ceiling height
 * @param {number} roofArea - Roof/floor area (m²)
 * @param {number} wallArea - Total wall surface area (m²)
 * @returns {number} Estimated room volume (m³)
 */
export function calculateRoomVolume(roofArea, wallArea = 0) {
  // If no wall area provided, use default ceiling height of 2.8m
  if (wallArea <= 0) {
    return roofArea * 2.8;
  }

  // Estimate ceiling height from wall area
  // Assuming roughly square room: perimeter ≈ 4 × √(roofArea)
  // wallArea ≈ perimeter × height
  // Therefore: height ≈ wallArea / (4 × √(roofArea))
  const estimatedPerimeter = 4 * Math.sqrt(Math.max(roofArea, 1));
  const estimatedHeight = wallArea / estimatedPerimeter;

  // Clamp height to reasonable range (1.5m to 4m)
  const ceilingHeight = Math.max(1.5, Math.min(4, estimatedHeight));

  return roofArea * ceilingHeight;
}

/**
 * Calculate heat loss from air ventilation
 * @param {number} roomTemperature - Current room temperature (°C)
 * @param {number} outsideTemperature - Outside temperature (°C)
 * @param {number} roomVolume - Room volume (m³)
 * @param {number} airChangesPerHour - Air changes per hour (ACH) (e.g., 2 = air replaced twice per hour)
 * @returns {number} Heat loss in watts
 */
export function calculateVentilationLoss(roomTemperature, outsideTemperature, roomVolume, airChangesPerHour = 2) {
  const temperatureDifference = roomTemperature - outsideTemperature;
  const hourToSecond = 1 / 3600;

  return D_AIR * roomVolume * CP_AIR * temperatureDifference * hourToSecond * airChangesPerHour;
}

/**
 * Calculate heat loss through building envelope
 * @param {number} uValue - Overall U-value (W/(m²·K))
 * @param {number} area - Total envelope area (m²)
 * @param {number} roomTemperature - Room temperature (°C)
 * @param {number} outsideTemperature - Outside temperature (°C)
 * @param {number} roofArea - Roof area (m²)
 * @returns {Object} Detailed heat loss breakdown
 */
export function calculateEnvelopeLoss(
  uValue,
  area,
  roomTemperature,
  outsideTemperature,
  roofArea
) {

  const tempDiff = roomTemperature - outsideTemperature;

  const wallArea = (area - roofArea) * 0.5;
  const lossWest = uValue * wallArea * tempDiff;
  const lossEast = uValue * wallArea * tempDiff;
  const lossNorth = uValue * roofArea * tempDiff;
  const lossSouth = 0;

  const totalLoss = lossWest + lossEast + lossNorth + lossSouth;

  return {
    total: totalLoss,
    west: lossWest,
    east: lossEast,
    north: lossNorth,
    south: lossSouth,
  };
}

/**
 * Calculate radiator heat output using iterative method
 * @param {Object} params - Calculation parameters
 * @returns {Object} Heat transfer results
 */
export function calculateRadiatorHeatTransfer({
  supplyTemperature,
  roomTemperature,
  volumetricFlow,
  valvePosition,
  thermostatOpening,
  radiatorCount,
  radiatorType,
}) {
  const iterations = 3;
  let returnTemperature = supplyTemperature - 5;

  const massFlow = D_WATER * volumetricFlow * valvePosition * thermostatOpening;
  const nominalPower = calculateRadiatorPower(radiatorCount, radiatorType);
  const radiatorCoefficient = nominalPower / Math.pow(50, RADIATOR_EXPONENT);

  // Iterative calculation for return temperature
  for (let i = 0; i < iterations; i++) {
    const meanTemperature = (supplyTemperature + returnTemperature) * 0.5;
    const tempDifference = Math.max(meanTemperature - roomTemperature, 0);
    const heatOutput = radiatorCoefficient * Math.pow(tempDifference, RADIATOR_EXPONENT);

    returnTemperature = supplyTemperature - heatOutput / (massFlow * CP_WATER + 0.0000001);
  }

  returnTemperature = Math.max(returnTemperature, roomTemperature);

  const meanTemperature = (supplyTemperature + returnTemperature) * 0.5;
  const tempDifference = Math.max(meanTemperature - roomTemperature, 0);

  const heatByWater = massFlow * CP_WATER * (supplyTemperature - returnTemperature);
  const heatByRadiator = radiatorCoefficient * Math.pow(tempDifference, RADIATOR_EXPONENT);
  const actualHeat = Math.min(heatByWater, heatByRadiator);

  return {
    heatOutput: actualHeat,
    returnTemperature,
    massFlow,
  };
}

/**
 * Update room temperature based on heat balance
 * @param {Object} params - Temperature update parameters
 * @returns {Object} Updated temperature and thermostat state
 */
export function updateRoomTemperature({
  currentTemperature,
  targetTemperature,
  heatInput,
  heatLoss,
  thermostatOpening,
  roomHeatCapacity,
}) {
  const netHeat = heatInput - heatLoss;
  const temperatureChange = netHeat / roomHeatCapacity;
  const newTemperature = currentTemperature + temperatureChange;

  // Proportional thermostat control
  const error = targetTemperature - newTemperature;
  const newThermostatOpening = Math.max(0, Math.min(1, thermostatOpening + 0.1 * error));

  return {
    temperature: newTemperature,
    temperatureChange,
    thermostatOpening: newThermostatOpening,
  };
}

/**
 * Format color string with alpha value
 * @param {string} colorTemplate - Color template with {alpha} placeholder
 * @param {number} alpha - Alpha value (0-1)
 * @returns {string} Formatted color string
 */
export function formatColorAlpha(colorTemplate, alpha) {
  return colorTemplate.replace("{alpha}", alpha.toString());
}

/**
 * Clamp value between min and max
 * @param {number} value - Value to clamp
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} Clamped value
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}
